import { type FocusEvent, type FormEvent } from 'react'
import { FormikErrors, FormikProps, FormikTouched } from 'formik'

import { PlatformSelector } from '../components/PlatformSelector'
import type { LogVolunteeringActivityFormFields } from '../LogVolunteeringActivity.types'

import classes from './LogVolunteeringActivityForm.module.scss'
import { useLogVolunteeringActivityForm } from './useLogVolunteeringActivityForm'
import { LogOnPlatformActivityForm } from './LogOnPlatformActivityForm'
import { LogOffPlatformActivityForm } from './LogOffPlatformActivityForm'
import { VolunteeringTimeLogType } from '@percent/workplace-giving/api/opportunity/dto'
import type { Organisation } from '@percent/workplace-giving/api/search/searchOrganisations/searchOrganisations.types'
import type {
  OpportunityDetailDto,
  OpportunitySelfParticipationDto
} from '@percent/workplace-giving/api/opportunity/dto'

export type LogVolunteeringActivityFormProps = Readonly<{
  values: LogVolunteeringActivityFormFields
  touched: FormikTouched<LogVolunteeringActivityFormFields>
  errors: FormikErrors<LogVolunteeringActivityFormFields>
  handleChange: (e: FormEvent<HTMLInputElement>) => void
  handleBlur: <A = HTMLInputElement>(e: FocusEvent<A>) => void
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  formikInstance: FormikProps<LogVolunteeringActivityFormFields>
  showPlatformSelector: boolean
  allowMultipleEntries: boolean
  allowOpportunityChange: boolean
  showWorkingHoursCheck: boolean
  workingHoursRequired: boolean
  duplicateDates?: string[]
  defaultOrg?: Organisation
  defaultOpportunity?: OpportunityDetailDto
  defaultCountryCode: string
  requireNonprofitFields: boolean | null
  searchOrgsWithEligibilityCheck: boolean | null
  disableCountryChange: boolean
  preloadedOpportunity?: OpportunitySelfParticipationDto
  showOverlapWarning?: boolean
  showRewardPreview?: boolean
}>

export function LogVolunteeringActivityForm({
  values,
  touched,
  errors,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  formikInstance,
  showPlatformSelector,
  allowMultipleEntries,
  allowOpportunityChange,
  showWorkingHoursCheck,
  workingHoursRequired,
  duplicateDates,
  defaultOrg,
  defaultOpportunity,
  defaultCountryCode,
  requireNonprofitFields,
  searchOrgsWithEligibilityCheck,
  disableCountryChange,
  preloadedOpportunity,
  showOverlapWarning,
  showRewardPreview
}: Readonly<LogVolunteeringActivityFormProps>) {
  const {
    isFetchingRegisteredOpportunities,
    isAwaitingFetchingRegisteredOpportunities,
    fetchingRegisteredOpportunitiesError,
    registeredOpportunitiesQuery,
    registeredOpportunitiesData,
    proposedRegisteredOpportunities,
    setRegisteredOpportunitiesQuery,
    isLoading,
    registeredOpportunitiesCount,
    selectedOpportunityDetails,
    setSelectedOpportunityDetails
  } = useLogVolunteeringActivityForm({
    values,
    setFieldValue,
    setFieldTouched,
    defaultOpportunity
  })

  return (
    <form className={classes.Form} onSubmit={event => event.preventDefault()}>
      {showPlatformSelector ? (
        <PlatformSelector value={values.platform} onChange={platform => setFieldValue('platform', platform)} />
      ) : null}

      {values.platform === VolunteeringTimeLogType.OnPlatform ? (
        <LogOnPlatformActivityForm
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          handleBlur={handleBlur}
          formikInstance={formikInstance}
          allowMultipleEntries={allowMultipleEntries}
          duplicateDates={duplicateDates}
          showWorkingHoursCheck={showWorkingHoursCheck}
          workingHoursRequired={workingHoursRequired}
          allowOpportunityChange={allowOpportunityChange}
          registeredOpportunitiesCount={registeredOpportunitiesCount}
          registeredOpportunitiesData={preloadedOpportunity ? [preloadedOpportunity] : registeredOpportunitiesData}
          setRegisteredOpportunitiesQuery={setRegisteredOpportunitiesQuery}
          registeredOpportunitiesQuery={registeredOpportunitiesQuery}
          proposedRegisteredOpportunities={proposedRegisteredOpportunities}
          isFetchingRegisteredOpportunities={isFetchingRegisteredOpportunities}
          fetchingRegisteredOpportunitiesError={fetchingRegisteredOpportunitiesError}
          selectedOpportunity={selectedOpportunityDetails}
          setSelectedOpportunity={setSelectedOpportunityDetails}
          isLoading={isLoading}
          isAwaitingFetchingRegisteredOpportunities={isAwaitingFetchingRegisteredOpportunities}
          showOverlapWarning={showOverlapWarning}
          showRewardPreview={showRewardPreview}
        />
      ) : (
        <LogOffPlatformActivityForm
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          handleBlur={handleBlur}
          formikInstance={formikInstance}
          allowMultipleEntries={allowMultipleEntries}
          duplicateDates={duplicateDates}
          showWorkingHoursCheck={showWorkingHoursCheck}
          workingHoursRequired={workingHoursRequired}
          defaultOrg={defaultOrg}
          defaultCountryCode={defaultCountryCode}
          disableCountryChange={disableCountryChange}
          requireNonprofitFields={requireNonprofitFields}
          searchOrgsWithEligibilityCheck={searchOrgsWithEligibilityCheck}
          showOverlapWarning={showOverlapWarning}
          showRewardPreview={showRewardPreview}
        />
      )}
    </form>
  )
}
